import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

const LAYER_ORDER = [
  "quotes", "ohlcv", "technicals", "hmm", "chart", "fundamentals", "chain", "wheel", "talk", "shadow",
];

function WheelWaterfall() {
  const [rep, setRep] = useState(null);
  useEffect(() => {
    api.get("/admin/wheel-exclusion-waterfall").then(({ data }) => setRep(data)).catch(() => setRep(null));
  }, []);
  if (!rep) return <div data-testid="wheel-waterfall">CSP exclusion waterfall: loading…</div>;
  const csp7 = rep.slices?.CSP_dte_7 || {};
  const csp30 = rep.slices?.CSP_dte_30 || {};
  const fmt = (w) => Object.entries(w.waterfall || {}).map(([k, n]) => `${k}:${n}`).join(" · ") || "—";
  return (
    <div className="space-y-1" data-testid="wheel-waterfall">
      <div>CSP exclusion waterfall (filter_revision {rep.filter_revision})</div>
      <div>DTE 7 passed {csp7.passed ?? "—"} · {fmt(csp7)}</div>
      <div>DTE 30 passed {csp30.passed ?? "—"} · {fmt(csp30)}</div>
      <div>DTE 38 passed {(rep.slices?.CSP_dte_38 || {}).passed ?? "—"} · {fmt(rep.slices?.CSP_dte_38 || {})}</div>
    </div>
  );
}

function stateColor(state) {
  if (state === "ready") return "#34D399";
  if (state === "partial" || state === "stale" || state === "queued" || state === "running") return "#F59E0B";
  if (state === "failed" || state === "missing") return "#F87171";
  return "var(--hoip-text-tertiary)";
}

export default function CacheCoverageDashboard({ houseProvider, onReloadProvider }) {
  const [dash, setDash] = useState(null);
  const [busy, setBusy] = useState("");
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoadError(null);
    setLoading(true);
    try {
      const { data } = await api.get("/admin/cache-coverage", { timeout: 10000 });
      setDash(data);
    } catch (err) {
      console.error(err);
      const timedOut = err?.code === "ECONNABORTED" || err?.response?.status === 504;
      setLoadError(timedOut
        ? "Cache coverage timed out. Retry — this view never loads full OHLCV or chain bodies."
        : "Cache coverage failed to load. Retry.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const run = async (path, label) => {
    setBusy(label);
    try {
      const { data } = await api.post(path);
      if (data?.queued) {
        await load();
      } else if (data?.skipped) {
        alert("A refresh is already running for that layer. Overlapping same-layer jobs are skipped.");
      }
      await load();
      if (onReloadProvider) await onReloadProvider();
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail) || `${label} failed`);
    } finally {
      setBusy("");
    }
  };

  const layers = dash?.layers || {};
  const cap = dash?.capability || {};
  const fdn = cap.financialdata_net || {};

  return (
    <div className="space-y-3" data-testid="cache-coverage-dashboard">
      {loadError ? (
        <div className="text-[11px] flex items-center gap-2" style={{ color: "#F87171" }}
          data-testid="cache-coverage-error">
          <span>{loadError}</span>
          <Button size="sm" variant="outline" onClick={load} disabled={loading}
            className="h-7 rounded-sm text-xs" data-testid="cache-coverage-retry">Retry</Button>
        </div>
      ) : null}
      {loading && !dash && !loadError ? (
        <div className="text-[11px]" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="cache-coverage-loading">
          Loading coverage metadata…
        </div>
      ) : null}
      {(!loadError || dash) ? (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] font-mono-data">
        <div className="border p-2" style={{ borderColor: "var(--hoip-border)" }}>
          <div style={{ color: "var(--hoip-text-tertiary)" }}>Unique universe</div>
          <div className="text-sm">{dash?.unique ?? "—"}</div>
        </div>
        <div className="border p-2" style={{ borderColor: "var(--hoip-border)" }}>
          <div style={{ color: "var(--hoip-text-tertiary)" }}>Portfolio (global)</div>
          <div className="text-sm">{dash?.holdings ?? "—"}</div>
        </div>
        <div className="border p-2" style={{ borderColor: "var(--hoip-border)" }}>
          <div style={{ color: "var(--hoip-text-tertiary)" }}>Watchlists (global)</div>
          <div className="text-sm">{dash?.watchlists ?? "—"}</div>
        </div>
        <div className="border p-2" style={{ borderColor: "var(--hoip-border)" }}>
          <div style={{ color: "var(--hoip-text-tertiary)" }}>Predefined unique</div>
          <div className="text-sm">{dash?.predefined_unique ?? "—"}</div>
        </div>
      </div>
      ) : null}

      <div className="overflow-x-auto border" style={{ borderColor: "var(--hoip-border)" }}>
        <table className="w-full text-[10px] font-mono-data" data-testid="cache-layer-table">
          <thead>
            <tr style={{ color: "var(--hoip-text-tertiary)" }}>
              <th className="text-left px-2 py-1">Layer</th>
              <th className="text-right px-2 py-1">Ready</th>
              <th className="text-right px-2 py-1">Eligible</th>
              <th className="text-right px-2 py-1">Stale</th>
              <th className="text-right px-2 py-1">Missing</th>
              <th className="text-left px-2 py-1">State</th>
              <th className="text-left px-2 py-1">Last success</th>
            </tr>
          </thead>
          <tbody>
            {LAYER_ORDER.map((id) => {
              const L = layers[id] || {};
              return (
                <tr key={id} className="border-t" style={{ borderColor: "var(--hoip-border)" }}>
                  <td className="px-2 py-1">{L.label || id}</td>
                  <td className="text-right px-2 py-1">{L.ready ?? "—"}</td>
                  <td className="text-right px-2 py-1">{L.eligible ?? "—"}</td>
                  <td className="text-right px-2 py-1">{L.stale ?? "—"}</td>
                  <td className="text-right px-2 py-1">{L.missing ?? "—"}</td>
                  <td className="px-2 py-1" style={{ color: stateColor(L.state) }}>{L.state || "—"}</td>
                  <td className="px-2 py-1" style={{ color: "var(--hoip-text-tertiary)" }}>{L.last_success ? String(L.last_success).slice(0, 19) : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button size="sm" disabled={!!busy} onClick={() => run("/admin/scanner-refresh", "quotes")}
          className="h-7 bg-white text-black hover:bg-gray-200 rounded-sm text-xs">{busy === "quotes" ? "Running…" : "Run quote refresh"}</Button>
        <Button size="sm" disabled={!!busy} onClick={() => run("/admin/ohlcv-refresh", "ohlcv")}
          className="h-7 bg-white text-black hover:bg-gray-200 rounded-sm text-xs">{busy === "ohlcv" ? "Running…" : "Run OHLCV/technicals batch"}</Button>
        <Button size="sm" disabled={!!busy} onClick={() => run("/admin/hmm-refresh", "hmm")}
          className="h-7 bg-white text-black hover:bg-gray-200 rounded-sm text-xs">{busy === "hmm" ? "Running…" : "Run HMM batch"}</Button>
        <Button size="sm" disabled={!!busy} onClick={() => run("/admin/chain-refresh", "chain")}
          className="h-7 bg-white text-black hover:bg-gray-200 rounded-sm text-xs">{busy === "chain" ? "Queued…" : "Run chain/Wheel batch"}</Button>
        <Button size="sm" disabled={!!busy} onClick={() => run("/admin/intel-refresh", "intel")}
          className="h-7 bg-white text-black hover:bg-gray-200 rounded-sm text-xs">{busy === "intel" ? "Queued…" : "Run intelligence refresh"}</Button>
        <Button size="sm" disabled={!!busy} onClick={() => run("/admin/fundamentals-refresh", "fundamentals")}
          className="h-7 bg-white text-black hover:bg-gray-200 rounded-sm text-xs">{busy === "fundamentals" ? "Queued…" : "Run fundamentals batch"}</Button>
        <Button size="sm" disabled={!!busy} onClick={() => run("/admin/capability-audit", "audit")}
          className="h-7 border rounded-sm text-xs" variant="outline">{busy === "audit" ? "Auditing…" : "Run provider field audit"}</Button>
      </div>

      <div className="text-[10px] font-mono-data space-y-1" style={{ color: "var(--hoip-text-tertiary)" }}>
        <div>Jobs use per-layer leases. Quote refresh no longer blocks OHLCV. Admin buttons enqueue and return immediately. RSS {houseProvider?.memory?.rss_mb ? `${houseProvider.memory.rss_mb} MB / 2048` : "—"}.</div>
        {dash?.jobs?.quotes?.vendor_calls != null && <div>Last quote job vendor calls: {dash.jobs.quotes.vendor_calls} · {dash.jobs.quotes.duration_s}s · RSS {dash.jobs.quotes.rss_mb ?? "—"} MB · load1 {dash.jobs.quotes.cpu_load1 ?? "—"}</div>}
        {["ohlcv", "hmm", "chain", "fundamentals"].map((k) => {
          const j = dash?.jobs?.[k];
          if (!j) return null;
          const reasons = j.fail_reasons && Object.keys(j.fail_reasons).length
            ? Object.entries(j.fail_reasons).map(([r, n]) => `${r}:${n}`).join(" · ")
            : null;
          return (
            <div key={k}>Last {k}: ok {j.ok ?? "—"} fail {j.errors ?? "—"} cursor {j.cursor ?? "—"}
              {j.blocked_on_ohlcv != null ? ` · blocked_on_ohlcv ${j.blocked_on_ohlcv}` : ""}
              {j.rate_limit_failures ? ` · rate-limit ${j.rate_limit_failures}` : ""}
              {reasons ? ` · ${reasons}` : ""}
              {j.fail_tickers?.length ? ` · samples ${j.fail_tickers.slice(0, 8).join(",")}` : ""}
            </div>
          );
        })}
        <WheelWaterfall />
        {(dash?.bootstrap_queues) && (
          <div data-testid="bootstrap-queues">
            Bootstrap queues
            {["quotes", "ohlcv", "hmm", "chain", "fundamentals"].map((k) => {
              const q = dash.bootstrap_queues[k] || {};
              return (
                <span key={k}> · {k} {q.depth ?? 0} (explicit {q.explicit ?? 0})</span>
              );
            })}
          </div>
        )}
        {(dash?.demand_batches || []).length > 0 && (
          <div data-testid="demand-batches">
            In-flight demand
            {(dash.demand_batches || []).map((b, i) => (
              <div key={b.id || i}>
                {b.kind} {b.id} · {b.status} · queued {b.queued} done {b.done}
                {b.watchlist ? ` · ${b.watchlist}` : ""}
                {b.target_dte != null ? ` · DTE ${b.target_dte}` : ""}
              </div>
            ))}
          </div>
        )}
        {dash?.ondemand_fallback && (
          <div data-testid="ondemand-fallback">
            On-demand fallback SLA hmm {dash.ondemand_fallback.sla_min?.hmm}m ·
            fundamentals {dash.ondemand_fallback.sla_min?.fundamentals}m ·
            chain {dash.ondemand_fallback.sla_min?.chain}m ·
            engaged 24h {dash.ondemand_fallback.engaged_24h ?? 0}
            {(dash.ondemand_fallback.last || []).slice(0, 5).map((ev, i) => (
              <div key={i}>{ev.layer} {ev.ticker} {ev.outcome} age {ev.age_min}m</div>
            ))}
          </div>
        )}
        {dash?.capability_audited_at && <div>Provider field audit: {String(dash.capability_audited_at).slice(0, 19)}</div>}
        {fdn.layers?.option_bid_ask_last && (
          <div>
            Option last/bid/ask (sample {cap.sample_ticker || "AMZN"}): last {fdn.layers.option_bid_ask_last.last_present ? "yes" : "no"} ·
            bid {fdn.layers.option_bid_ask_last.bid_present ? "yes" : "no"} ·
            ask {fdn.layers.option_bid_ask_last.ask_present ? "yes" : "no"}.
            OI from FinancialData.net listing: no. User wording: End-of-day / delayed — not live.
          </div>
        )}
      </div>
    </div>
  );
}
